import type { RealMachinesDatabase } from '@/db/database'
import { getDatabase } from '@/db/database'
import { DatabaseOperationError, executeDatabaseOperation } from '@/db/errors'
import { normalizePhone, normalizeSearchText, toActiveKey } from '@/db/normalization'
import {
  createTechnicianInputSchema,
  technicianSchema,
  updateTechnicianInputSchema,
} from '@/db/schemas'
import { createUuid } from '@/db/uuid'
import type { CreateTechnicianInput, Technician, UpdateTechnicianInput } from '@/db/types'
import { changedFields, requireRecord, writeAuditLog } from './helpers'

export class TechnicianRepository {
  constructor(private readonly database: RealMachinesDatabase = getDatabase()) {}

  async create(input: CreateTechnicianInput): Promise<Technician> {
    return executeDatabaseOperation('criar técnico', async () => {
      const data = createTechnicianInputSchema.parse(input)
      return this.database.transaction(
        'rw',
        [this.database.technicians, this.database.signatures, this.database.auditLogs],
        async () => {
          const id = createUuid()
          if (data.signatureId) {
            const signature = requireRecord(
              await this.database.signatures.get(data.signatureId),
              'Assinatura',
            )
            if (signature.technicianId !== id || signature.type !== 'technician') {
              throw new DatabaseOperationError(
                'relationship',
                'A assinatura informada não pertence ao novo técnico.',
              )
            }
          }
          const now = new Date().toISOString()
          const technician = technicianSchema.parse({
            ...data,
            id,
            normalizedName: normalizeSearchText(data.name),
            normalizedPhone: normalizePhone(data.phone),
            active: true,
            activeKey: 1,
            createdAt: now,
            updatedAt: now,
          })
          await this.database.technicians.add(technician)
          await writeAuditLog(this.database, 'technician', id, 'create', {}, now)
          return technician
        },
      )
    })
  }

  async getById(id: string): Promise<Technician | undefined> {
    return executeDatabaseOperation('consultar técnico', async () => {
      const technician = await this.database.technicians.get(id)
      return technician ? technicianSchema.parse(technician) : undefined
    })
  }

  async listActive(limit = 100): Promise<Technician[]> {
    return executeDatabaseOperation('listar técnicos ativos', async () => {
      const pageSize = Math.min(Math.max(Math.trunc(limit), 1), 100)
      const technicians = await this.database.technicians
        .where('[activeKey+normalizedName]')
        .between([1, ''], [1, '\uffff'])
        .limit(pageSize)
        .toArray()
      return technicians.map((technician) => technicianSchema.parse(technician))
    })
  }

  async list(
    options: { active?: boolean; limit?: number; offset?: number } = {},
  ): Promise<Technician[]> {
    return executeDatabaseOperation('listar técnicos', async () => {
      const limit = Math.min(Math.max(options.limit ?? 50, 1), 100)
      const offset = Math.max(options.offset ?? 0, 0)
      const technicians =
        options.active !== undefined
          ? await this.database.technicians
              .where('activeKey')
              .equals(options.active ? 1 : 0)
              .offset(offset)
              .limit(limit)
              .toArray()
          : await this.database.technicians
              .orderBy('updatedAt')
              .reverse()
              .offset(offset)
              .limit(limit)
              .toArray()
      return technicians.map((technician) => technicianSchema.parse(technician))
    })
  }

  async search(
    query: string,
    options: number | { limit?: number; includeInactive?: boolean } = 30,
  ): Promise<Technician[]> {
    return executeDatabaseOperation('pesquisar técnicos', async () => {
      const normalizedText = normalizeSearchText(query)
      const normalizedDigits = normalizePhone(query)
      if (!normalizedText && !normalizedDigits) return []
      const pageSize = Math.min(
        Math.max(typeof options === 'number' ? options : (options.limit ?? 30), 1),
        500,
      )
      const includeInactive =
        typeof options === 'number' ? false : (options.includeInactive ?? false)
      const searches = [
        this.database.technicians
          .where('normalizedName')
          .startsWith(normalizedText)
          .limit(pageSize)
          .toArray(),
      ]
      if (normalizedDigits) {
        searches.push(
          this.database.technicians
            .where('normalizedPhone')
            .startsWith(normalizedDigits)
            .limit(pageSize)
            .toArray(),
        )
      }
      const groups = await Promise.all(searches)
      const unique = new Map(groups.flat().map((record) => [record.id, record]))
      return [...unique.values()]
        .filter((record) => includeInactive || record.active)
        .slice(0, pageSize)
        .map((record) => technicianSchema.parse(record))
    })
  }

  async update(id: string, input: UpdateTechnicianInput): Promise<Technician> {
    return executeDatabaseOperation('editar técnico', async () => {
      const data = updateTechnicianInputSchema.parse(input)
      return this.database.transaction(
        'rw',
        [this.database.technicians, this.database.signatures, this.database.auditLogs],
        async () => {
          const existing = technicianSchema.parse(
            requireRecord(await this.database.technicians.get(id), 'Técnico'),
          )
          if (data.signatureId) {
            const signature = requireRecord(
              await this.database.signatures.get(data.signatureId),
              'Assinatura',
            )
            if (signature.technicianId !== id || signature.type !== 'technician') {
              throw new DatabaseOperationError(
                'relationship',
                'A assinatura não pertence ao técnico.',
              )
            }
          }
          const now = new Date().toISOString()
          const technician = technicianSchema.parse({
            ...existing,
            ...data,
            normalizedName: normalizeSearchText(data.name ?? existing.name),
            normalizedPhone: normalizePhone(data.phone ?? existing.phone),
            updatedAt: now,
          })
          await this.database.technicians.put(technician)
          await writeAuditLog(this.database, 'technician', id, 'update', changedFields(data), now)
          return technician
        },
      )
    })
  }

  async countActive(): Promise<number> {
    return executeDatabaseOperation('contar técnicos ativos', () =>
      this.database.technicians.where('activeKey').equals(1).count(),
    )
  }

  async inactivate(id: string, options: { allowLastActive?: boolean } = {}): Promise<Technician> {
    return executeDatabaseOperation('inativar técnico', async () =>
      this.database.transaction(
        'rw',
        [this.database.technicians, this.database.auditLogs],
        async () => {
          const existing = technicianSchema.parse(
            requireRecord(await this.database.technicians.get(id), 'Técnico'),
          )
          if (!existing.active) return existing
          const activeCount = await this.database.technicians.where('activeKey').equals(1).count()
          if (activeCount === 1 && !options.allowLastActive) {
            throw new DatabaseOperationError(
              'invalid_state',
              'Este é o único técnico ativo.',
              'Confirme de forma especial para deixar o aplicativo sem técnico ativo.',
            )
          }
          const now = new Date().toISOString()
          const technician = technicianSchema.parse({
            ...existing,
            active: false,
            activeKey: toActiveKey(false),
            updatedAt: now,
          })
          await this.database.technicians.put(technician)
          await writeAuditLog(this.database, 'technician', id, 'inactivate', {}, now)
          return technician
        },
      ),
    )
  }

  async reactivate(id: string): Promise<Technician> {
    return executeDatabaseOperation('reativar técnico', async () =>
      this.database.transaction(
        'rw',
        [this.database.technicians, this.database.auditLogs],
        async () => {
          const existing = technicianSchema.parse(
            requireRecord(await this.database.technicians.get(id), 'Técnico'),
          )
          if (existing.active) return existing
          const now = new Date().toISOString()
          const technician = technicianSchema.parse({
            ...existing,
            active: true,
            activeKey: toActiveKey(true),
            updatedAt: now,
          })
          await this.database.technicians.put(technician)
          await writeAuditLog(
            this.database,
            'technician',
            id,
            'update',
            { fields: ['active'], from: false, to: true },
            now,
          )
          return technician
        },
      ),
    )
  }

  async delete(id: string): Promise<void> {
    return executeDatabaseOperation('apagar técnico', async () =>
      this.database.transaction(
        'rw',
        [
          this.database.technicians,
          this.database.inspections,
          this.database.signatures,
          this.database.auditLogs,
        ],
        async () => {
          const technician = technicianSchema.parse(
            requireRecord(await this.database.technicians.get(id), 'Técnico'),
          )
          if (technician.active) {
            throw new DatabaseOperationError(
              'invalid_state',
              'Técnico ativo não pode ser apagado; inative primeiro.',
            )
          }
          const [inspectionCount, signatureCount] = await Promise.all([
            this.database.inspections.where('technicianId').equals(id).count(),
            this.database.signatures.where('technicianId').equals(id).count(),
          ])
          if (inspectionCount || signatureCount) {
            throw new DatabaseOperationError('protected_record', 'Técnico possui vínculos.')
          }
          const now = new Date().toISOString()
          await this.database.technicians.delete(id)
          await writeAuditLog(this.database, 'technician', id, 'delete', {}, now)
        },
      ),
    )
  }
}
